import { useCallback } from 'react';

import { Command } from '@dreryk/command-query-api';

import { Bean, PromiseVoidFunction, SortOrder, TranslatableText } from '@dreryk/react-common';
import { List, TestId } from '@dreryk/react-components';

import { ExtractItemId, FormsTemplateListItem, FormsTemplateSortItemsFn } from '../FormsTemplates.types';
import { useFormsTemplatesContext } from '../formsTemplatesContext';
import { sortWithEmptyItemsLast } from '../FormsTemplates.utils';
import { formsTemplatesTranslationKeys } from '../translations';

import { FormsTemplatesAddNewItemLink } from './FormsTemplatesAddNewItemLink';
import { FormsTemplatesListItemsRenderer } from './FormsTemplatesListItemsRenderer';

type FormsTemplatesListProps<ITEM extends Bean> = {
   readonly onTemplateRemoved: PromiseVoidFunction;
   readonly sortFn: FormsTemplateSortItemsFn<ITEM>;
   readonly removeConfirmationTitle: TranslatableText;
   readonly removeSuccessMessage: TranslatableText;
   readonly removeCommandCreator: <RESULT>(templateId: ExtractItemId<ITEM>) => Command<RESULT>;
   readonly testId: TestId;
};

export const FormsTemplatesList = <ITEM extends Bean>(props: FormsTemplatesListProps<ITEM>): JSX.Element | null => {
   const { items } = useFormsTemplatesContext<FormsTemplateListItem<ITEM>>();
   const sortWithNewItemLast = useCallback<FormsTemplateSortItemsFn<ITEM>>(
      (itemsToSort, sortOrder) => sortWithEmptyItemsLast(itemsToSort, sortOrder, props.sortFn),
      [props.sortFn]
   );

   if (!items.length) {
      return null;
   }

   return (
      <>
         <List testId={props.testId} headerText={formsTemplatesTranslationKeys.name} defaultSortOrder={SortOrder.ASC}>
            <List.Sort<FormsTemplateListItem<ITEM>> sortFn={sortWithNewItemLast} items={items}>
               {(sortedItems) => (
                  <FormsTemplatesListItemsRenderer<ITEM>
                     items={sortedItems}
                     onTemplateRemoved={props.onTemplateRemoved}
                     removeConfirmationTitle={props.removeConfirmationTitle}
                     removeSuccessMessage={props.removeSuccessMessage}
                     removeCommandCreator={props.removeCommandCreator}
                  />
               )}
            </List.Sort>
         </List>
         <FormsTemplatesAddNewItemLink />
      </>
   );
};
